import { useMemo, useState, useEffect } from 'react';
import {
  getBestPriceByAirline,
  getMostConvenientFlight,
} from '@shared/helpers/flights/flights';
import { hasManyOptions } from '@shared/pages/Flights/FlightsSearch/helpers';

const hasValidResults = results => results && Object.keys(results).length > 1;

const buildClusters = results =>
  results.clusters?.map(cluster => {
    const clusterDetails = results.object_clusters[cluster.cluster_id];

    const legs = cluster.legs.map(leg => {
      const legDetails = results.object_legs[leg.hash];

      const options = leg.options.map(option => ({
        ...option,
        ...results.object_options[option.hash],
      }));

      return {
        ...legDetails,
        options,
      };
    });

    return {
      ...cluster,
      operation_id: results.operation_id,
      cluster: clusterDetails,
      legs,
    };
  }) || [];

const processClustersWithDuplicates = clusters => {
  const seenPrices = {};
  const duplicates = {};

  const getClusterTotalFlightTime = cluster => {
    return cluster.legs.reduce((total, leg) => {
      const shortestOption = leg.options.reduce((min, option) =>
        option.flight_time < min.flight_time ? option : min
      );
      return total + shortestOption.flight_time;
    }, 0);
  };

  const filtered = clusters.filter(item => {
    const price = item.cluster.composition_price.total;

    const hasManyOptionsInCluster = item.legs.some(leg =>
      hasManyOptions(leg.options)
    );

    // 🔹 Caso 1: muchas opciones → marcar en duplicates
    if (hasManyOptionsInCluster) {
      if (!duplicates[item.cluster_id]) {
        duplicates[item.cluster_id] = [];
      }
      duplicates[item.cluster_id].push(item);
    }

    // 🔹 Caso 2: duplicados por precio
    if (seenPrices[price]) {
      const existing = seenPrices[price];

      const currentTime = getClusterTotalFlightTime(item);
      const existingTime = getClusterTotalFlightTime(existing);

      // 👉 si el nuevo es más rápido → reemplaza al existente
      if (currentTime < existingTime) {
        // mover el viejo a duplicates
        if (!duplicates[item.cluster_id]) {
          duplicates[item.cluster_id] = [];
        }
        duplicates[item.cluster_id].push(existing);

        // guardar el nuevo como el mejor
        seenPrices[price] = item;

        return true;
      } else {
        // 👉 el nuevo es peor → va a duplicates
        if (!duplicates[existing.cluster_id]) {
          duplicates[existing.cluster_id] = [existing];
        }
        duplicates[existing.cluster_id].push(item);

        return false;
      }
    }

    // 🔹 primer cluster con ese precio
    seenPrices[price] = item;
    return true;
  });

  return { filtered, duplicates };
};

const addDuplicateFlag = (clusters, duplicates) =>
  clusters.map(item => ({
    ...item,
    hasDuplicates: Boolean(duplicates[item.cluster_id]),
  }));

const getCurrencyInfo = (clusters, results) => {
  const fareCurrency =
    clusters?.[0]?.cluster?.composition_price?.price_detail?.[0]?.fare_currency;

  return {
    can_sale_in_usd: results.can_sale_in_usd,
    isInUSD: fareCurrency === 'USD',
    isInARS: fareCurrency === 'ARS',
  };
};

const useProcessedSearchResults = searchResults => {
  const [flightResultFiltered, setFlightResultFiltered] = useState([]);

  // 🔹 1. Procesamiento puro
  const { optimizedClusters, duplicates, flightsInUSD } = useMemo(() => {
    if (!hasValidResults(searchResults)) {
      return {
        optimizedClusters: [],
        duplicates: {},
        flightsInUSD: {},
      };
    }

    const clusters = buildClusters(searchResults);
    const { filtered, duplicates } = processClustersWithDuplicates(clusters);

    const optimizedClusters = addDuplicateFlag(filtered, duplicates);

    const flightsInUSD = getCurrencyInfo(clusters, searchResults);

    return { optimizedClusters, duplicates, flightsInUSD };
  }, [searchResults]);

  // 🔹 2. Sync inicial / cuando cambia searchResults
  useEffect(() => {
    setFlightResultFiltered(optimizedClusters);
  }, [optimizedClusters]);

  // 🔹 3. Derivados del filtered (NO state)
  const bestPriceByAirline = useMemo(
    () =>
      flightResultFiltered.length
        ? getBestPriceByAirline(flightResultFiltered)
        : [],
    [flightResultFiltered]
  );

  const bestFastConvenient = useMemo(
    () =>
      flightResultFiltered.length
        ? getMostConvenientFlight(flightResultFiltered)
        : null,
    [flightResultFiltered]
  );

  return {
    optimizedClusters,
    flightResultFiltered,
    duplicates,
    setFlightResultFiltered,
    bestPriceByAirline,
    bestFastConvenient,
    flightsInUSD,
  };
};

export default useProcessedSearchResults;
